/**
 * Горячие клавиши и быстрый переход: назначения хранятся на этом устройстве (localStorage),
 * как и настройки голоса, — у ноутбука и у игрового компьютера клавиатуры разные.
 */
import { create } from 'zustand'
import { desktopPlatform } from '@/lib/desktop'
import { conflictOf, effectiveBindings, parseBindings, type Bindings, type Combo, type HotkeyAction } from '@/lib/hotkeys'
import { pushRecent } from '@/lib/quickSwitch'

const KEY = 'klichat.hotkeys'
const RECENT_KEY = 'klichat.recent'

/** Мак: подписи значками ⌘⇧ и Cmd вместо Ctrl в сочетаниях по умолчанию. */
export const isMac = desktopPlatform === 'darwin' || (typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent))

function readSaved(): Bindings {
  try {
    return parseBindings(localStorage.getItem(KEY))
  } catch {
    return {}
  }
}

function writeSaved(b: Bindings): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(b))
  } catch {
    /* приватный режим: назначения доживут до перезагрузки */
  }
}

function readRecent(): string[] {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown
    return Array.isArray(list) ? list.filter((k): k is string => typeof k === 'string').slice(0, 20) : []
  } catch {
    return []
  }
}

interface HotkeyState {
  saved: Bindings
  bindings: Record<HotkeyAction, Combo | null>
  /** В настройках записывают сочетание: горячие клавиши молчат, чтобы нажатое не сработало. */
  recording: boolean
  switcherOpen: boolean
  /** Недавно открытые места для пустого быстрого перехода: g:, c:, d: + id. */
  recent: string[]
  /** Назначить (null — снять). Возвращает действие, с которого сочетание пришлось снять. */
  assign: (action: HotkeyAction, combo: Combo | null) => HotkeyAction | null
  reset: (action: HotkeyAction) => void
  setRecording: (on: boolean) => void
  setSwitcherOpen: (open: boolean) => void
  rememberPlace: (key: string) => void
}

export const useHotkeyStore = create<HotkeyState>((set, get) => {
  const saved = readSaved()
  const store = (next: Bindings) => {
    writeSaved(next)
    set({ saved: next, bindings: effectiveBindings(next, isMac) })
  }
  return {
    saved,
    bindings: effectiveBindings(saved, isMac),
    recording: false,
    switcherOpen: false,
    recent: readRecent(),
    assign: (action, combo) => {
      const next: Bindings = { ...get().saved, [action]: combo }
      const other = combo ? conflictOf(get().bindings, action, combo) : null
      if (other) next[other] = null
      store(next)
      return other
    },
    reset: (action) => {
      const next = { ...get().saved }
      delete next[action]
      // сочетание по умолчанию могли отдать другому действию — снимаем его там
      const def = effectiveBindings({}, isMac)[action]
      const other = def ? conflictOf(effectiveBindings(next, isMac), action, def) : null
      if (other) next[other] = null
      store(next)
    },
    setRecording: (recording) => set({ recording }),
    setSwitcherOpen: (switcherOpen) => set({ switcherOpen }),
    rememberPlace: (key) => {
      const recent = pushRecent(get().recent, key)
      set({ recent })
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(recent))
      } catch {
        /* приватный режим */
      }
    },
  }
})
